import { Box, Button, Card, Flex, Group, Loader, Modal, Stack, Text, TextInput, Title, Tooltip } from "@mantine/core";
import { IProjectDashboard, IUserDepricated } from "@mctypes/global.types";
import type { IAccess, IAccessPopulated } from "@mcapps/shared";
import {
  IconDeviceFloppy,
  IconHelpCircle,
  IconPencil,
  IconPlus,
  IconSettings,
  IconTrash,
  IconX,
} from "@tabler/icons-react";
import * as R from "ramda";
import React, { useEffect, useRef, useState } from "react";
import { Prompt, useHistory } from "react-router-dom";
import snarkdown from "snarkdown";
import { apiSlice } from "../../../apiSlice";
import { ConfirmDialog, DropdownFilter } from "../../../components/";
import AccessHandlerModal from "../../../components/AccessComp/AccessComp";
import CustomGrid, { allWidgetsArray, IWidgetTemplate } from "../../../components/CustomGrid/CustomGrid";
import McDropdownNew from "../../../components/McDropdown/MCDropdownNew";
import { selectUserMember } from "../../../global/globalSlice";
import { useAppDispatch, useAppSelector } from "../../../hooks";
import { AccessLevelsEnum } from "../../../mc-shared/enums";
import { MCApplicationsUrlEnum } from "../../../mc-shared/sharedConstants";
import { debounce, sortArrayByString } from "../../utils";
import { doLogUsage } from "../admin/restQueries";
import {
  useDoRemoveLayoutMutation,
  useDoSaveNewLayoutMutation,
  useDoUpdateLayoutMutation,
  useFetchLayoutsQuery,
} from "./ProjectDashboardRTK";
import { dashboardInfo } from "./projectDashboardConstants";
import { setProjectDashboardActiveLayoutInRedux, setProjectDashBoardEditModeInRedux } from "./projectDashboardSlice";

export const getLayoutById = (allLayouts: IProjectDashboard[], layoutId: string) => {
  return allLayouts && allLayouts.find((item) => item._id === layoutId);
};

const ProjectDashboard: React.FC = () => {
  const project = useAppSelector((state) => state.adminReducer.project);
  const editMode = useAppSelector((state) => state.projectDashboardReducer.editMode);
  const activeLayout = useAppSelector((state) => state.projectDashboardReducer.activeLayout);
  const [showInfoModal, setShowInfoModal] = useState<boolean>(false);
  const [showNewModal, setShowNewModal] = useState<boolean>(false);
  const [allWidgets, setAllWidgets] = useState<IWidgetTemplateDropdown[]>([]);
  const [errorFlag, setErrorFlag] = useState<string>("");
  const [disablePrompt, setDisablePrompt] = useState<boolean>(false);
  const [confirmSaveOpen, setConfirmSaveOpen] = useState<boolean>(false);
  const previousLayout = useRef(null);
  const dispatch = useAppDispatch();
  const userMember = useAppSelector(selectUserMember);
  const [doSaveNewLayout, { data: _newLayout }] = useDoSaveNewLayoutMutation();
  const [doUpdateLayout] = useDoUpdateLayoutMutation();
  const [doRemoveLayout] = useDoRemoveLayoutMutation();
  const { data: allLayouts, isFetching } = useFetchLayoutsQuery(project._id);
  const sortedLayouts: IProjectDashboard[] = sortArrayByString(allLayouts, "title");
  interface IWidgetTemplateDropdown extends IWidgetTemplate {
    isActive: boolean;
    value: string;
  }

  const history = useHistory();

  useEffect(() => {
    doLogUsage(project, MCApplicationsUrlEnum.DASHBOARD, "open");
    if (activeLayout?._id != null) {
      dispatch(setProjectDashBoardEditModeInRedux(false));
    }
  }, []);

  const getLayoutIdFromUrl = () => {
    const indexOfId = history.location.search.indexOf("layout");
    if (indexOfId === -1) {
      return null;
    }
    const indexOf = history.location.search.indexOf("=");
    const id = history.location.search.substring(indexOf + 1);
    return id;
  };
  // Load the active dashboard layout on mount: prioritize URL param, then project default, or restore previous active layout
  useEffect(() => {
    if (allLayouts?.length > 0 && activeLayout == null) {
      const layoutIdFromUrl = getLayoutIdFromUrl();
      const layout = getLayoutById(allLayouts, layoutIdFromUrl);

      if (layout != null) {
        dispatch(setProjectDashboardActiveLayoutInRedux(layout));
        return;
      }

      const activeLayout = allLayouts.find((item) => item.isProjectDefault === true);
      dispatch(setProjectDashboardActiveLayoutInRedux(activeLayout));
    } else {
      if (activeLayout?._id) {
        dispatch(setProjectDashboardActiveLayoutInRedux(getLayoutById(allLayouts, activeLayout._id)));
      }
    }
  }, [allLayouts]);

  // Initialize available widgets list by filtering project-specific widgets from the global widget registry
  useEffect(() => {
    if (allWidgets.length === 0) {
      const filteredByProject = allWidgetsArray.filter((item) =>
        item.hasOwnProperty("limitToProject") ? item.limitToProject === project._id : true,
      );
      setAllWidgets(filteredByProject.map((item) => ({ ...item, value: item.i, isActive: false })));
    }
  }, [allWidgets]);

  // Sync widget active state with the current layout's grid when entering edit mode
  useEffect(() => {
    if (activeLayout != null && editMode === true) {
      const layoutGrid = activeLayout.layoutGrid;
      const updateAllWidgets = allWidgets.map((obj) => {
        const exists = layoutGrid.some((item) => item.i === obj.i);
        exists ? (obj.isActive = true) : (obj.isActive = false);
        return obj;
      });
      setAllWidgets(updateAllWidgets);
    }
  }, [activeLayout, editMode]);

  const onLocationChange = () => {
    return "Du har ulagrede endringer, er du sikker på at du vil forlate siden?";
  };

  const setQueryStringWithoutPageReload = (queryParamKey: string, qsValue: string) => {
    const queryString = `${history.location.pathname}?${queryParamKey}=${qsValue}`;
    if (qsValue != null && history.location.pathname + history.location.search !== queryString) {
      history.push(queryString);
    }
  };

  const handleRemoveWidget = (key: string) => {
    const updateLayoutGrid = activeLayout.layoutGrid.filter((obj) => {
      return obj.i !== key;
    });
    const layout = { ...activeLayout, layoutGrid: updateLayoutGrid };
    dispatch(setProjectDashboardActiveLayoutInRedux(layout));
  };

  const handleAddWidget = (item) => {
    const updateLayoutGrid = [...activeLayout.layoutGrid];
    let copyOfItem = R.clone(item);
    copyOfItem.isActive = true;
    copyOfItem.i = item.value;
    updateLayoutGrid.push(copyOfItem);
    const layout = { ...activeLayout, layoutGrid: updateLayoutGrid };
    dispatch(setProjectDashboardActiveLayoutInRedux(layout));
  };

  const handleNewLayout = async (newLayout) => {
    setShowNewModal(false);
    previousLayout.current = activeLayout;
    dispatch(setProjectDashBoardEditModeInRedux(true));
    dispatch(setProjectDashboardActiveLayoutInRedux(newLayout));
  };

  const handleToggleEditMode = () => {
    previousLayout.current = activeLayout;
    dispatch(setProjectDashBoardEditModeInRedux(true));
  };

  const handleAddRemoveWidget = (value: string) => {
    const item = allWidgets.find((obj) => {
      return obj.value === value;
    });
    if (item.isActive === true) {
      handleRemoveWidget(item.value);
    } else {
      handleAddWidget(item);
    }
  };

  const handleCancel = () => {
    setErrorFlag(null);

    dispatch(setProjectDashboardActiveLayoutInRedux({ ...previousLayout.current, layoutGrid: [] }));
    //force layout grid rerender rerender
    setTimeout(() => {
      dispatch(setProjectDashboardActiveLayoutInRedux({ ...previousLayout.current }));
    }, 50);
    dispatch(apiSlice.util.invalidateTags(["ProjectDashboard"]));

    dispatch(setProjectDashBoardEditModeInRedux(false));
  };

  const saveLayout = async () => {
    try {
      setDisablePrompt(true);
      if (allLayouts.some((item) => item.title === activeLayout.title && item._id !== activeLayout._id)) {
        return setErrorFlag("editModeTitle");
      }
      if (activeLayout?._id) {
        doUpdateLayout({
          projectId: project._id,
          newLayout: activeLayout,
        }).unwrap();
      } else {
        //save new layout
        const res = await doSaveNewLayout({
          projectId: project._id,
          newLayoutAccessPopulated: activeLayout,
        }).unwrap();
        if (res) {
          setQueryStringWithoutPageReload("layout", res._id);
          dispatch(setProjectDashboardActiveLayoutInRedux(res));
        }
      }
    } catch (error) {
      console.log("error", error);
      dispatch(setProjectDashboardActiveLayoutInRedux(previousLayout.current));
    }
    return dispatch(setProjectDashBoardEditModeInRedux(false));
  };

  const goToLayout = (layoutId: string) => {
    history.replace(`/project/${project._id}/dashboard?layout=${layoutId}`);
  };

  const handleRemoveLayout = async () => {
    const resp = await doRemoveLayout({ projectId: project._id, layoutId: activeLayout._id });
    if (resp) {
      dispatch(setProjectDashBoardEditModeInRedux(false));
    }
  };

  const debounceValue = debounce((value) => {
    if (errorFlag === "editModeTitle") {
      setErrorFlag(null);
    }
    dispatch(setProjectDashboardActiveLayoutInRedux({ ...activeLayout, title: value }));
  }, 500);

  const onChange = (e) => {
    debounceValue(e.target.value);
  };
  const userIsCreatorOrAdmin =
    userMember?.group === "ADMIN" || (userMember?.user as IUserDepricated)._id === activeLayout?.userId;

  const handleUpdateAccess = async (updatedAccess: IAccessPopulated) => {
    if (activeLayout?._id) {
      const access: IAccess = {
        level: updatedAccess.level,
        groups: updatedAccess.groups,
        users: updatedAccess.users.map((user) => user._id),
      };
      dispatch(setProjectDashboardActiveLayoutInRedux({ ...activeLayout, access: access }));
    } else {
      dispatch(setProjectDashboardActiveLayoutInRedux({ ...activeLayout, access: updatedAccess }));
    }
  };

  const changeLayout = (layoutId: string) => {
    goToLayout(layoutId);
    dispatch(setProjectDashboardActiveLayoutInRedux(getLayoutById(allLayouts, layoutId)));
  };

  const CreateNewModal: React.FC<{
    isOpen: boolean;
    toggle: () => void;
    createNew: (newLayout: IProjectDashboard) => void;
  }> = ({ isOpen, toggle, createNew }) => {
    const [newTitle, setNewTitle] = useState<string>("");
    const [selectedLayout, setSelectedLayout] = useState<IProjectDashboard>(null);
    const [access, setAccess] = useState<IAccessPopulated>({
      level: AccessLevelsEnum.PROJECT,
      groups: [],
      users: [],
    });

    return (
      <Modal opened={isOpen} onClose={toggle} title="Ny dashboard" size="md">
        <Stack gap="md">
          <TextInput
            autoFocus
            onChange={(e) => setNewTitle(e.target.value)}
            value={newTitle}
            placeholder="Skriv inn navn på dashboard"
          />
          <McDropdownNew
            onChange={(_, layout) => setSelectedLayout(layout)}
            options={sortedLayouts}
            displayKey="title"
            defaultTitle={allLayouts?.length > 0 ? "Kopier innstillinger fra..." : "Ingen dashboard å kopiere fra"}
            currentValue={selectedLayout}
            size="md"
            justify="space-between"
          />
          <Group justify="space-between">
            <AccessHandlerModal
              allUsers={project.members.map((m) => m.user)}
              isEditable={true}
              access={access}
              updateAccess={(updatedAccess) => setAccess(updatedAccess)}
              editMode={true}
              buttonSize={"md"}
            />
            <Button
              leftSection={<IconDeviceFloppy size={16} />}
              color="green"
              onClick={() =>
                createNew({
                  ...selectedLayout,
                  title: newTitle,
                  _id: null,
                  isMCAppsPreset: false,
                  isProjectDefault: false,
                  layoutGrid: selectedLayout?.layoutGrid || [],
                  access: access,
                })
              }
            >
              Opprett
            </Button>
          </Group>
        </Stack>
      </Modal>
    );
  };

  return (
    <>
      {<Prompt when={editMode && disablePrompt === false} message={onLocationChange} />}
      <Stack p="xs" gap={"xxs"} h="100%" w="100%" pos="relative">
        <Card px="md" mx="10px" pt="sm" pb={"xxs"} shadow="xs" style={{ overflow: "visible" }} radius="sm">
          <Flex justify="space-between" align="flex-start">
            <Box>
              <Title order={4} fw={400}>
                Velkommen til {project.maconomy.name}
              </Title>
              <Text size="sm" c="dimmed" fs="italic">
                {project.members.length} prosjektmedlemmer
              </Text>
            </Box>

            <Stack gap="xs" style={{ flex: "0 0 auto" }}>
              <Group justify="flex-end" align="center" gap={"xs"}>
                {activeLayout?.access && activeLayout?.isProjectDefault !== true && (
                  <AccessHandlerModal
                    allUsers={project.members.map((m) => m.user)}
                    isEditable={true}
                    access={activeLayout?.access as IAccessPopulated}
                    updateAccess={(updatedAccess) => handleUpdateAccess(updatedAccess)}
                    editMode={editMode}
                  />
                )}
                {editMode ? (
                  <Group gap="xs">
                    <DropdownFilter
                      color="mc-blue"
                      filterOptions={allWidgets}
                      title={"velg widgets"}
                      onClick={(title, value) => {
                        handleAddRemoveWidget(value);
                      }}
                    />
                    <Box pos="relative">
                      <TextInput
                        placeholder="Tittel"
                        id="editModeTitle"
                        value={activeLayout?.title}
                        onChange={onChange}
                      />
                      {errorFlag === "editModeTitle" && (
                        <Text c="red" size="sm" pos="absolute">
                          Navn er allerede i bruk!
                        </Text>
                      )}
                    </Box>
                  </Group>
                ) : (
                  <McDropdownNew
                    color="mc-primary"
                    variant="filled"
                    currentValue={activeLayout}
                    options={sortedLayouts}
                    displayKey="title"
                    onChange={(oldValue, newValue) => changeLayout(newValue._id)}
                  />
                )}
                <Modal
                  opened={showInfoModal}
                  onClose={() => setShowInfoModal(false)}
                  title={"Konfigurer Dashboard"}
                  size="60vw"
                >
                  <Card style={{ overflow: "visible", border: "1px solid #e2e2e2" }} radius="sm">
                    <div className="dashboard-info" dangerouslySetInnerHTML={{ __html: snarkdown(dashboardInfo) }} />
                  </Card>
                </Modal>
                <CreateNewModal
                  isOpen={showNewModal}
                  toggle={() => setShowNewModal(false)}
                  createNew={handleNewLayout}
                />
              </Group>

              <Group justify="flex-end" gap={"0px"}>
                {editMode === false && (
                  <>
                    <Tooltip label="Nytt dashboard">
                      <Button variant="subtle" size="sm" px={"xs"} color="green" onClick={() => setShowNewModal(true)}>
                        <IconPlus size={18} />
                      </Button>
                    </Tooltip>
                    <Tooltip label="Rediger dashboard">
                      <Button
                        onClick={() => handleToggleEditMode()}
                        variant="subtle"
                        color="mc-primary"
                        size="sm"
                        px={"xs"}
                      >
                        <IconPencil size={18} />
                      </Button>
                    </Tooltip>
                  </>
                )}
                {editMode === true && (
                  <>
                    <Tooltip label="Lagre">
                      <Button
                        size="sm"
                        mr={"xxs"}
                        disabled={activeLayout?.title === ""}
                        onClick={() => setConfirmSaveOpen(true)}
                      >
                        <IconDeviceFloppy size={16} />
                      </Button>
                    </Tooltip>

                    <Tooltip label="Avbryt">
                      <Button
                        variant="light"
                        color="warmgray.8"
                        size="sm"
                        px={"xs"}
                        onClick={() => handleCancel()}
                        mr={"xxs"}
                      >
                        <IconX size={14} />
                      </Button>
                    </Tooltip>
                  </>
                )}
                {editMode === true && userIsCreatorOrAdmin && (
                  <ConfirmDialog
                    title="Slette oppsett?"
                    message="Er du sikker på at du vil slette dette oppsettet?"
                    confirmCb={() => handleRemoveLayout()}
                  >
                    <Tooltip label="Slett dashboard">
                      <Button variant="subtle" size="sm" px={"xs"} color="red">
                        <IconTrash size={18} />
                      </Button>
                    </Tooltip>
                  </ConfirmDialog>
                )}

                <Tooltip label="Hjelp">
                  <Button
                    variant="subtle"
                    color="warmgray.8"
                    size="sm"
                    px={"xs"}
                    onClick={() => setShowInfoModal(true)}
                  >
                    <IconHelpCircle size={18} />
                  </Button>
                </Tooltip>
                {userMember?.group === "ADMIN" && (
                  <Tooltip label="Dashboard-innstillinger">
                    <Button
                      variant="subtle"
                      color="warmgray.8"
                      size="sm"
                      px={"xs"}
                      onClick={() => history.push(`/project/${project?._id}/settings/general`)}
                    >
                      <IconSettings size={18} />
                    </Button>
                  </Tooltip>
                )}
              </Group>
            </Stack>
          </Flex>
        </Card>

        <Box
          style={{
            flex: 1,
            overflow: "auto",
            outline: editMode ? "4px dashed #ec5c5c" : "",
            borderRadius: "7px",
          }}
        >
          {isFetching && (
            <Flex justify="center" align="center" h="100%">
              <Loader size="xl" />
            </Flex>
          )}
          {activeLayout != null && isFetching === false && (
            <CustomGrid
              layoutGrid={activeLayout?.layoutGrid}
              setLayout={(grid) =>
                dispatch(setProjectDashboardActiveLayoutInRedux({ ...activeLayout, layoutGrid: grid }))
              }
            />
          )}
        </Box>
      </Stack>
      <Modal opened={confirmSaveOpen} onClose={() => setConfirmSaveOpen(false)} title="Bekreft lagring" centered>
        <Text mb="md">Dette vil endre dashboardet for alle i prosjektet, er du sikker på at du vil fortsette?</Text>

        <Group justify="flex-end">
          <Button
            variant="light"
            onClick={() => {
              setConfirmSaveOpen(false);
              dispatch(setProjectDashBoardEditModeInRedux(false));
            }}
          >
            Avbryt
          </Button>

          <Button
            color="blue"
            onClick={() => {
              setConfirmSaveOpen(false);
              saveLayout();
            }}
          >
            Lagre
          </Button>
        </Group>
      </Modal>
    </>
  );
};

export default ProjectDashboard;
